import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { useClientTiers } from '../auth/ClientTiersProvider';
import { useBrand } from '../theme/BrandProvider';
import { AccountSwitcher } from '../components/AccountSwitcher';
import { OwnAccountOnlyNotice } from '../components/OwnAccountOnlyNotice';
import { satisfiesTiers } from '../auth/TierGuard';
import { useNavBadges } from './useNavBadges';

/**
 * One entry in the flat client nav. `requiredTier` hides the item unless the caller holds that
 * tier; `anyOfTiers` (#20070) hides it unless the caller holds ANY of the listed tiers. The filter
 * is presentation — TierGuard on the route and the controller's authorisation are the enforcement.
 */
export interface ClientNavItem {
  path: string;
  label: string;
  icon: string;
  requiredTier?: string;
  anyOfTiers?: readonly string[];
  /**
   * #20979 (BR-T-F33-001) — a count shown beside the label, e.g. work orders awaiting approval. Filled at render
   * from {@link useNavBadges}; absent (no badge) when the count is zero or its fetch failed. #20977 reuses it.
   */
  badge?: number;
}

const navItems: ClientNavItem[] = [
  { path: '/dashboard', label: 'Dashboard', icon: '📊' },
  // #20979 — Work order approvals (MVP slice). No requiredTier: every client tier reads the list (BR-WOA-001);
  // only ClientAdmin decides, on the detail screen (#10279). Badged with the count awaiting approval.
  { path: '/work-orders', label: 'Work orders', icon: '🛠️' },
  // #20977 — Change approvals. No requiredTier: every client tier reads the list (BR-CRA-002); only ClientAdmin
  // decides, on the detail screen (#10270). Badged with the count awaiting the client's sign-off.
  { path: '/change-approvals', label: 'Change approvals', icon: '📝' },
  { path: '/cases', label: 'My Tickets', icon: '🎫' },
  { path: '/invoices', label: 'Invoices', icon: '💰' },
  { path: '/it-admin/quotes', label: 'Quotes', icon: '📄', requiredTier: 'ClientAdmin' },
  { path: '/it-admin/departments', label: 'Departments', icon: '🏢', requiredTier: 'ClientAdmin' },
  { path: '/m365-users', label: 'Users', icon: '👥' },
  { path: '/licences', label: 'Licences', icon: '🔑' },
  // #16316 — no requiredTier: browsing the catalogue is informational and open to all three
  // client tiers. The ClientAdmin gate belongs to the basket/checkout actions in S2 (#16317).
  { path: '/shop', label: 'Shop', icon: '🛒' },
  // #16317 — the cart IS the active draft basket. requiredTier, unlike Shop above: browsing is
  // informational and open to every client tier, but the cart and "Send for pricing" are a
  // commercial act and the API is ClientAdmin-only. The filter here is presentation; TierGuard on
  // the route and the controller's authorisation are the enforcement.
  { path: '/cart', label: 'Cart', icon: '🧺', requiredTier: 'ClientAdmin' },
  { path: '/domains', label: 'Domains & DNS', icon: '🌐' },
  { path: '/security-posture', label: 'Security', icon: '🛡️' },
  // #20070 — F11.5 self-service (spec §9.2). Two items in this FLAT list: the prototype's "Tenant"
  // heading is a prototype grouping, not a nav feature this release adds. My requests is
  // ClientAdmin OR ClientUser (ClientAdminAndUser) and never ClientBilling; Approvals is
  // ClientAdmin only (ClientAdminOnly). Each renders an In-Development page until its screen lands.
  { path: '/self-service/requests', label: 'My requests', icon: '📨', anyOfTiers: ['ClientAdmin', 'ClientUser'] },
  { path: '/self-service/approvals', label: 'Approvals', icon: '✅', requiredTier: 'ClientAdmin' },
  { path: '/settings', label: 'Settings', icon: '⚙️' },
];

/**
 * Bug #22240 (BR-CMOB-001 / BR-CMOB-002, operator decision C1) — the three signed-in shell layouts,
 * mirroring admin #22190 (BR-MOB-001 / BR-MOB-010):
 * - `phone`   (< 768px)      — the top bar holds ONLY the menu button and the logo. The menu button
 *                               opens the nav as a drawer: the account switcher, the nav items, then
 *                               the signed-in user and Sign out.
 * - `tablet`  (768 – 1023px) — the sidebar leaves the layout flow (content is full width) and opens
 *                               as an overlay from the menu button; the top bar keeps the account
 *                               switcher and the user menu.
 * - `desktop` (>= 1024px)    — unchanged: the 256px sidebar is persistent and there is no menu button.
 *
 * Breakpoints are Tailwind's `md` (768) and `lg` (1024). jsdom implements no `matchMedia`, so the
 * fallback is `desktop` — every existing test that renders the shell keeps today's layout, and the
 * tests for the narrow layouts mock `matchMedia` (same posture as the admin AdminLayout).
 */
export type ClientShellViewport = 'phone' | 'tablet' | 'desktop';

const MD_QUERY = '(min-width: 768px)';
const LG_QUERY = '(min-width: 1024px)';

function readViewport(): ClientShellViewport {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'desktop';
  if (window.matchMedia(LG_QUERY).matches) return 'desktop';
  return window.matchMedia(MD_QUERY).matches ? 'tablet' : 'phone';
}

function useShellViewport(): ClientShellViewport {
  const [viewport, setViewport] = useState<ClientShellViewport>(readViewport);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const queries = [window.matchMedia(MD_QUERY), window.matchMedia(LG_QUERY)];
    const update = () => setViewport(readViewport());
    update();
    queries.forEach(q => q.addEventListener('change', update));
    return () => queries.forEach(q => q.removeEventListener('change', update));
  }, []);
  return viewport;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const SignOutIcon = () => (
  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

export function ClientLayout() {
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const [navOpen, setNavOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const wasNavOpen = useRef(false);
  const viewport = useShellViewport();
  const isDesktop = viewport === 'desktop';
  const isPhone = viewport === 'phone';
  const { displayName, email, logout } = useAuth();
  const { tiers, hasTier } = useClientTiers();
  const { assets } = useBrand();
  const navBadges = useNavBadges();
  const visibleNavItems: ClientNavItem[] = navItems
    .filter((item) => satisfiesTiers(hasTier, item.requiredTier, item.anyOfTiers))
    .map((item) => ({ ...item, badge: navBadges[item.path] }));

  const initials = displayName
    ? displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : email?.slice(0, 2).toUpperCase() ?? '??';

  const closeNav = useCallback(() => setNavOpen(false), []);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    }
    if (profileOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [profileOpen]);

  // Growing to 1024px and up docks the sidebar, so an open drawer (and its backdrop) must not linger.
  useEffect(() => {
    if (isDesktop) setNavOpen(false);
  }, [isDesktop]);

  // #22240 AC3 — while the drawer is open, focus moves into it, Tab / Shift+Tab stay inside it and
  // Escape closes it. Focus returns to the menu button when it closes (effect below).
  useEffect(() => {
    if (!navOpen) return;
    const drawer = drawerRef.current;
    if (!drawer) return;
    const focusables = () => Array.from(drawer.querySelectorAll<HTMLElement>(FOCUSABLE));
    focusables()[0]?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (!drawer) return;
      if (e.key === 'Escape') {
        // An open account-switcher menu inside the drawer owns this Escape: it closes only the menu.
        if (drawer.querySelector('[aria-haspopup="menu"][aria-expanded="true"]')) return;
        e.preventDefault();
        setNavOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      const inside = active instanceof Node && drawer.contains(active);
      if (e.shiftKey) {
        if (!inside || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (!inside || active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [navOpen, viewport]);

  // Return focus to the menu button whenever the drawer closes (Escape, backdrop, nav item, Sign out).
  useEffect(() => {
    if (wasNavOpen.current && !navOpen) menuButtonRef.current?.focus();
    wasNavOpen.current = navOpen;
  }, [navOpen]);

  const brandBlock = (
    <div className="px-4 pt-4 pb-3 border-b border-sand-200">
      <Link to="/" aria-label={assets.homeAriaLabel} className="block" onClick={isDesktop ? undefined : closeNav}>
        <img
          src={assets.logo}
          alt={assets.logoAlt}
          className="h-8 w-auto"
          width={183}
          height={59}
        />
      </Link>
      <p className="text-[10px] uppercase tracking-[0.2em] text-sage-500 mt-2">Client Portal</p>
    </div>
  );

  // One nav list for every layout — the same tier-filtered items in the same order (BR-CMOB-001).
  const navList = (
    <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-1">
      {visibleNavItems.map(item => (
        <NavLink key={item.path} to={item.path}
          onClick={isDesktop ? undefined : closeNav}
          className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium ${isActive ? 'bg-sand-100 text-fern-600' : 'text-gray-700 hover:bg-sand-50 hover:text-fern-600'}`}>
          <span>{item.icon}</span><span>{item.label}</span>
          {item.badge !== undefined && item.badge > 0 && (
            <span
              data-testid={`nav-badge-${item.path}`}
              className="ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-amber-100 px-1.5 text-xs font-semibold text-amber-900"
            >
              {item.badge}
              <span className="sr-only"> awaiting your approval</span>
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {isDesktop && (
        <aside className="flex w-64 flex-col bg-white border-r border-sand-200">
          {brandBlock}
          {navList}
        </aside>
      )}
      {!isDesktop && navOpen && (
        <>
          {/* Backdrop — a click or tap closes the drawer. Out of the tab order; Escape is the keyboard path. */}
          <button
            type="button"
            tabIndex={-1}
            aria-label="Close navigation"
            data-testid="client-nav-backdrop"
            className="fixed inset-0 z-40 bg-black/50"
            onClick={closeNav}
          />
          <div
            ref={drawerRef}
            id="client-nav-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            data-mode={isPhone ? 'drawer' : 'overlay'}
            className={`fixed inset-y-0 left-0 z-50 flex flex-col bg-white border-r border-sand-200 shadow-lg ${isPhone ? 'w-[min(320px,86vw)]' : 'w-64'}`}
          >
            {isPhone ? (
              <>
                {/* C1 — on a phone the account switcher moves from the top bar to the top of the drawer. */}
                <div className="px-3 pt-4 pb-2 border-b border-sand-200 empty:hidden">
                  <AccountSwitcher />
                </div>
                {navList}
                <div className="border-t border-sand-200 px-4 py-3" data-testid="client-nav-user">
                  <p className="text-sm font-medium text-gray-900">{displayName}</p>
                  <p className="text-xs text-sage-500 truncate">{email}</p>
                  <button
                    type="button"
                    onClick={() => { closeNav(); logout(); }}
                    className="mt-2 flex min-h-11 w-full items-center gap-2 rounded-md px-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <SignOutIcon />
                    Sign out
                  </button>
                </div>
              </>
            ) : (
              <>
                {brandBlock}
                {navList}
              </>
            )}
          </div>
        </>
      )}
      {/* `min-w-0` lets this column shrink to the viewport instead of to its widest child, so the
          shell itself never scrolls sideways at 375 / 768 (BR-CMOB-002). */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="h-16 flex items-center justify-between border-b border-sand-200 bg-white px-4">
          {!isDesktop && (
            <button
              ref={menuButtonRef}
              type="button"
              onClick={() => setNavOpen(true)}
              aria-label="Open navigation"
              aria-expanded={navOpen}
              aria-controls="client-nav-drawer"
              className="mr-2 inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md text-gray-700 hover:bg-sand-50"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}
          <Link to="/" aria-label={assets.homeAriaLabel} className={isDesktop ? 'md:hidden' : 'mr-3 flex-shrink-0'}>
            <img
              src={assets.logo}
              alt={assets.logoAlt}
              className="h-7 w-auto"
              width={183}
              height={59}
            />
          </Link>
          {/* #16306 — account context switcher: beside the brand, not buried in the
              profile menu, so the active client stays visible while working (AC-7).
              #22240 C1 — on a phone it lives at the top of the nav drawer instead. */}
          {!isPhone && <AccountSwitcher />}
          <div className="flex-1" />
          {!isPhone && (
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center gap-2 rounded-md p-1 hover:bg-sand-50"
                aria-label="User menu"
              >
                <div className="w-8 h-8 rounded-full bg-fern-600 flex items-center justify-center text-white text-xs font-medium">
                  {initials}
                </div>
                <span className="hidden lg:block text-sm font-medium text-gray-800">{displayName}</span>
              </button>

              {profileOpen && (
                <div className="absolute right-0 mt-2 w-64 rounded-lg border border-sand-200 bg-white shadow-lg z-50">
                  <div className="px-4 py-3 border-b border-sand-200">
                    <p className="text-sm font-medium text-gray-900">{displayName}</p>
                    <p className="text-xs text-sage-500 truncate">{email}</p>
                    {tiers.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {tiers.map(t => (
                          <span key={t} className="inline-flex items-center rounded bg-sand-100 px-1.5 py-0.5 text-[10px] font-medium text-fern-600">{t}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="py-1">
                    <button
                      onClick={() => { setProfileOpen(false); logout(); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <SignOutIcon />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </header>
        {/* BR-CMOB-002 — 16px side gutters below 768px (`p-4`); the existing 24px (`md:p-6`) from 768px
            up, so tablet and desktop spacing are unchanged. */}
        <main className="min-w-0 flex-1 overflow-y-auto p-4 md:p-6">
          <div className="mx-auto max-w-5xl">
            <OwnAccountOnlyNotice />
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
